import React from 'react';
import { ExecutionSchedule } from '../types';
import { format, parseISO } from 'date-fns';

interface ExecutionCardProps {
  execution: ExecutionSchedule;
  onStatusClick?: (executionId: string, currentStatus: string) => void;
}

const statusColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800 border-gray-300',
  running: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  partial: 'bg-orange-100 text-orange-800 border-orange-300',
  partially_completed: 'bg-orange-100 text-orange-800 border-orange-300',
  failed: 'bg-red-100 text-red-800 border-red-300',
};

const statusIcons: Record<string, string> = {
  pending: '⏳',
  running: '▶️',
  completed: '✅',
  partial: '⚠️',
  partially_completed: '⚠️',
  failed: '❌',
};

export const ExecutionCard: React.FC<ExecutionCardProps> = ({ execution, onStatusClick }) => {
  const scheduledTime = parseISO(execution.timestamp || execution.scheduled_time || '');
  const startTime = execution.actual_start ? parseISO(execution.actual_start) : null;
  const endTime = execution.actual_end ? parseISO(execution.actual_end) : null;

  return (
    <div className={`border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${statusColors[execution.status]}`}>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl shrink-0">{statusIcons[execution.status]}</span>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm break-words">{execution.workflow_name}</h3>
            <p className="text-xs opacity-75 truncate">{execution.account_name || execution.account_id}</p>
          </div>
        </div>
        <button
          onClick={() => onStatusClick?.(execution.execution_id, execution.status)}
          className="px-3 py-1 text-xs font-medium rounded-full border cursor-pointer hover:opacity-80 transition-opacity shrink-0"
        >
          {execution.status.toUpperCase()}
        </button>
      </div>

      {/* Time Info */}
      <div className="space-y-1 text-xs mb-3">
        <div className="flex items-center gap-2">
          <span className="opacity-75">📅 Scheduled:</span>
          <span className="font-medium">{format(scheduledTime, 'MMM dd, HH:mm')}</span>
        </div>
        {startTime && (
          <div className="flex items-center gap-2">
            <span className="opacity-75">▶️ Started:</span>
            <span className="font-medium">{format(startTime, 'HH:mm:ss')}</span>
          </div>
        )}
        {endTime && (
          <div className="flex items-center gap-2">
            <span className="opacity-75">🏁 Ended:</span>
            <span className="font-medium">{format(endTime, 'HH:mm:ss')}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="opacity-75">⏱️ Duration:</span>
          <span className="font-medium">~{execution.estimated_duration_minutes} min</span>
        </div>
      </div>

      {/* Actions List */}
      {execution.actions && execution.actions.length > 0 && (
        <div className="border-t pt-2">
          <p className="text-xs opacity-75 mb-1">Actions:</p>
          <div className="flex flex-wrap gap-1">
            {execution.actions.map((action, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-white bg-opacity-50 rounded text-xs">
                {action}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {execution.error_message && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          <strong>Error:</strong> {execution.error_message}
        </div>
      )}

      {/* Execution ID (for debugging) */}
      <div className="mt-2 text-xs opacity-50 truncate" title={execution.execution_id}>
        ID: {execution.execution_id.substring(0, 16)}...
      </div>
    </div>
  );
};
